import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

import {
  ICommentsPropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/CommentsPropertyTypeStrategy/types/ICommentsPropertyTypeInterfaces'
import {
  IDatabaseItemResponseInstance,
} from '@toolify/server/src/controllers/DatabaseItemController/types/IDatabaseItemResponseInstance'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {DatabasePropertyType} from '@toolify/server/src/models/mongoose/DatabasePropertyModel/enum/DatabasePropertyType'

function getTimestamp(value: Date | string | null | undefined): number | null {
  if(!value) {
    return null
  }
  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

function isLatestCommentRemoved(
  previousFullValue: ICommentsPropertyTypeInterfaces['fullValue'] | null,
  newFullValue: ICommentsPropertyTypeInterfaces['fullValue'],
): boolean {
  const previousLastUpdatedAt = getTimestamp(previousFullValue?.lastUpdatedAt)
  if(previousLastUpdatedAt === null) {
    return false
  }
  const newLastUpdatedAt = getTimestamp(newFullValue.lastUpdatedAt)
  return newLastUpdatedAt === null || newLastUpdatedAt < previousLastUpdatedAt
}

export function useCommentsValueTypeStrategy() {
  const authStore = useAuthStore()
  const strategyComponentType = PropertyStrategyComponentType.Dropdown

  const fullValueToText = (value: ICommentsPropertyTypeInterfaces['fullValue'], property: IDatabasePropertyResponseInstance) => {
    if(!value) {
      return '0'
    }
    return String(value.count || '')
  }

  const socketCellValueToClientCellValue = (
    previousValue: IDatabaseItemResponseInstance['cellValues'][string] | undefined,
    newValue: IDatabaseItemResponseInstance['cellValues'][string],
    triggerEntityId: string | null,
    databaseProperty?: {type?: DatabasePropertyType},
  ) => {
    if(databaseProperty?.type === DatabasePropertyType.Teleport) {
      return newValue
    }
    const previousFullValue = (previousValue?.value ?? null) as ICommentsPropertyTypeInterfaces['fullValue'] | null
    const newFullValue = (newValue?.value ?? null) as ICommentsPropertyTypeInterfaces['fullValue'] | null
    if(!newFullValue) {
      return newValue
    }
    if(previousValue?.value === undefined || (previousFullValue && previousFullValue.unseenCount === undefined)) {
      return newValue
    }
    const isOwnTrigger = authStore.currentEntity?.entityType === EntityType.User && authStore.currentEntity.entityId === triggerEntityId
    const previousUnseenCount = previousFullValue?.unseenCount ?? 0
    const newCount = newFullValue.count ?? 0
    const countDelta = newCount - (previousFullValue?.count ?? 0)
    let unseenCount = previousUnseenCount
    if(countDelta < 0) {
      unseenCount = !isOwnTrigger && isLatestCommentRemoved(previousFullValue, newFullValue)
        ? Math.max(0, Math.min(previousUnseenCount + countDelta, newCount))
        : Math.min(previousUnseenCount, newCount)
    } else if(!isOwnTrigger) {
      unseenCount = previousUnseenCount + countDelta
    }
    return {
      ...newValue,
      value: {
        ...newFullValue,
        unseenCount: newFullValue.unseenCount ?? unseenCount,
        hasUnseenMention: newFullValue.hasUnseenMention ?? previousFullValue?.hasUnseenMention ?? false,
      },
    }
  }

  return {
    fullValueToText,
    socketCellValueToClientCellValue,
    strategyComponentType,
  }
}
